import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  FileMetadata,
  StorageQuota,
  UploadFileRequest,
  UploadMultipleFilesRequest,
  UploadToOrganizationRequest,
  DownloadFileRequest,
  GetSignedUrlRequest,
  ListFilesRequest,
  DeleteFileRequest,
  DeleteMultipleRequest,
  DeleteFolderRequest,
  FileMoveRequest,
  FileCopyRequest,
  CreateFolderRequest,
  UploadFileResponse,
  UploadMultipleFilesResponse,
  SignedUrlResponse,
  FileListResponse,
  StorageQuotaResponse,
  FileOperationResult,
  ApiError,
  FileTreeItem,
  FolderData,
  InitiateResumableUploadRequest,
  InitiateResumableUploadResponse,
  UploadChunkRequest,
  UploadChunkResponse,
  FinalizeResumableUploadRequest,
  FinalizeResumableUploadResponse
} from './models/file-management.models';

@Injectable({
  providedIn: 'root'
})
export class FileManagementService {
  private readonly baseUrl = `${environment.apiConfig.baseUri}/file`;

  constructor(private http: HttpClient) {}

  // File Upload Methods
  uploadFile(file: File, folderPath?: string, uploadToAdmin = false): Observable<UploadFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (folderPath) {
      formData.append('folderPath', folderPath);
    }
    if (uploadToAdmin) {
      formData.append('uploadToAdmin', 'true');
    }

    return this.http.post<UploadFileResponse>(`${this.baseUrl}/upload`, formData)
      .pipe(catchError(this.handleError));
  }

  uploadMultipleFiles(files: File[], folderPath?: string, uploadToAdmin = false): Observable<UploadMultipleFilesResponse> {
    const formData = new FormData();

    files.forEach(file => {
      formData.append('files', file);
    });

    if (folderPath) {
      formData.append('folderPath', folderPath);
    }
    if (uploadToAdmin) {
      formData.append('uploadToAdmin', 'true');
    }

    return this.http.post<UploadMultipleFilesResponse>(`${this.baseUrl}/upload-multiple`, formData)
      .pipe(catchError(this.handleError));
  }

  uploadToOrganization(file: File, folderPath?: string): Observable<UploadFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (folderPath) {
      formData.append('folderPath', folderPath);
    }

    return this.http.post<UploadFileResponse>(`${this.baseUrl}/upload-organization`, formData)
      .pipe(catchError(this.handleError));
  }

  // File Download Methods
  downloadFile(filePath: string, fromAdmin = false): Observable<Blob> {
    let params = new HttpParams().set('filePath', filePath);
    if (fromAdmin) {
      params = params.set('fromAdmin', 'true');
    }

    return this.http.get(`${this.baseUrl}/download`, {
      params,
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  getSignedUrl(filePath: string, fromAdmin = false, fromOrganization = false, expirationMinutes = 5): Observable<SignedUrlResponse> {
    const request: GetSignedUrlRequest = {
      filePath,
      fromAdmin,
      fromOrganization,
      expirationMinutes
    };

    return this.http.post<SignedUrlResponse>(`${this.baseUrl}/signed-url`, request)
      .pipe(catchError(this.handleError));
  }

  // File Listing Methods
  listFiles(request: ListFilesRequest): Observable<FileListResponse> {
    return this.http.post<FileListResponse>(`${this.baseUrl}/list`, request)
      .pipe(catchError(this.handleError));
  }

  // File Management Methods
  deleteFile(filePath: string, fromAdmin = false): Observable<FileOperationResult> {
    const request: DeleteFileRequest = {
      filePath,
      fromAdmin
    };

    return this.http.post<FileOperationResult>(`${this.baseUrl}/delete`, request)
      .pipe(catchError(this.handleError));
  }

  deleteMultipleFiles(filePaths: string[], fromAdmin: boolean): Observable<FileOperationResult> {
    const request: DeleteMultipleRequest = {
      filePaths,
      fromAdmin
    };

    return this.http.post<FileOperationResult>(`${this.baseUrl}/delete-multiple`, request)
      .pipe(catchError(this.handleError));
  }

  deleteFolder(folderPath: string, fromAdmin = false, fromOrganization = false): Observable<FileOperationResult> {
    const request: DeleteFolderRequest = {
      folderPath,
      fromAdmin,
      fromOrganization
    };

    return this.http.post<FileOperationResult>(`${this.baseUrl}/delete-folder`, request)
      .pipe(catchError(this.handleError));
  }

  moveFile(sourcePath: string, destinationPath: string, moveFromAdmin = false, moveToAdmin = false): Observable<FileOperationResult> {
    const request: FileMoveRequest = {
      sourcePath,
      destinationPath,
      moveFromAdmin,
      moveToAdmin
    };

    return this.http.post<FileOperationResult>(`${this.baseUrl}/move`, request)
      .pipe(catchError(this.handleError));
  }

  copyFile(sourcePath: string, destinationPath: string, copyFromAdmin = false, copyToAdmin = false): Observable<FileOperationResult> {
    const request: FileCopyRequest = {
      sourcePath,
      destinationPath,
      copyFromAdmin,
      copyToAdmin
    };

    return this.http.post<FileOperationResult>(`${this.baseUrl}/copy`, request)
      .pipe(catchError(this.handleError));
  }

  createFolder(folderPath: string, inAdmin = false, inOrganization = false): Observable<FileOperationResult> {
    const request: CreateFolderRequest = {
      folderPath,
      inAdmin,
      inOrganization
    };

    return this.http.post<FileOperationResult>(`${this.baseUrl}/create-folder`, request)
      .pipe(catchError(this.handleError));
  }

  // Resumable/Chunked Upload Methods for Large Files
  /**
   * Initiates a resumable upload session for large files
   * @param file The file to upload
   * @param folderPath Optional folder path
   * @param uploadToAdmin Whether to upload to admin storage
   * @param uploadToOrganization Whether to upload to organization storage
   * @param chunkSize Optional chunk size in bytes (default: 5MB)
   */
  initiateResumableUpload(
    file: File,
    folderPath?: string,
    uploadToAdmin = false,
    uploadToOrganization = false,
    chunkSize = 5 * 1024 * 1024 // Default 5MB chunks
  ): Observable<InitiateResumableUploadResponse> {
    const request: InitiateResumableUploadRequest = {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type || 'application/octet-stream',
      folderPath,
      uploadToAdmin,
      uploadToOrganization,
      chunkSize
    };

    console.log('Initiating resumable upload request:', {
      fileName: file.name,
      fileSize: file.size,
      contentType: request.contentType,
      folderPath,
      uploadToAdmin,
      uploadToOrganization,
      chunkSize
    });

    return this.http.post<InitiateResumableUploadResponse>(`${this.baseUrl}/initiate-resumable-upload`, request)
      .pipe(
        catchError((error) => {
          console.error('HTTP error initiating upload:', error);
          return this.handleError(error);
        })
      );
  }

  /**
   * Uploads a single chunk of the file
   * @param uploadId The upload session ID
   * @param chunkIndex The 0-based index of the chunk
   * @param chunkData The chunk data as Blob
   * @param isLastChunk Whether this is the final chunk
   */
  uploadChunk(
    uploadId: string,
    chunkIndex: number,
    chunkData: Blob,
    isLastChunk: boolean
  ): Observable<UploadChunkResponse> {
    const formData = new FormData();
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', chunkIndex.toString());
    formData.append('chunk', chunkData);
    formData.append('isLastChunk', isLastChunk.toString());

    return this.http.post<UploadChunkResponse>(`${this.baseUrl}/upload-chunk`, formData)
      .pipe(catchError(this.handleError));
  }

  /**
   * Finalizes the resumable upload after all chunks are uploaded
   * @param uploadId The upload session ID
   */
  finalizeResumableUpload(uploadId: string): Observable<FinalizeResumableUploadResponse> {
    const request: FinalizeResumableUploadRequest = {
      uploadId
    };

    return this.http.post<FinalizeResumableUploadResponse>(`${this.baseUrl}/finalize-resumable-upload`, request)
      .pipe(catchError(this.handleError));
  }

  /**
   * Uploads a large file using chunked/resumable upload
   * This method handles the entire upload process automatically
   * @param file The file to upload
   * @param folderPath Optional folder path
   * @param uploadToAdmin Whether to upload to admin storage
   * @param uploadToOrganization Whether to upload to organization storage
   * @param chunkSize Optional chunk size in bytes (default: 5MB)
   * @param onProgress Optional progress callback (progress: 0-100)
   */
  uploadLargeFile(
    file: File,
    folderPath?: string,
    uploadToAdmin = false,
    uploadToOrganization = false,
    chunkSize = 5 * 1024 * 1024, // Default 5MB chunks
    onProgress?: (progress: number) => void
  ): Observable<UploadFileResponse> {
    return new Observable(observer => {
      let isCancelled = false;

      // Return cleanup function
      observer.add(() => {
        isCancelled = true;
      });

      // Step 1: Initiate upload
      this.initiateResumableUpload(file, folderPath, uploadToAdmin, uploadToOrganization, chunkSize)
        .subscribe({
          next: (initResponse) => {
            if (isCancelled) {
              observer.complete();
              return;
            }

            console.log('Initiate upload response:', initResponse);

            // Check if response has success field (new format) or required fields (direct format)
            const hasSuccessField = 'success' in initResponse;
            const hasRequiredFields = initResponse?.uploadId && initResponse?.totalChunks && initResponse?.chunkSize;

            if (!initResponse) {
              console.error('Empty response from server');
              observer.error(new Error('Empty response from server'));
              return;
            }

            // If response has success field, check it
            if (hasSuccessField && !initResponse.success) {
              const errorMessage = initResponse?.message || 'Failed to initiate upload. Please check backend logs.';
              console.error('Failed to initiate upload. Response:', initResponse);
              observer.error(new Error(errorMessage));
              return;
            }

            // Validate required fields (works for both formats)
            if (!hasRequiredFields) {
              console.error('Invalid initiate response - missing required fields:', initResponse);
              observer.error(new Error('Invalid response from server: missing uploadId, totalChunks, or chunkSize'));
              return;
            }

            console.log('Upload initiated:', {
              uploadId: initResponse.uploadId,
              totalChunks: initResponse.totalChunks,
              chunkSize: initResponse.chunkSize
            });

            const uploadId = initResponse.uploadId;
            const totalChunks = initResponse.totalChunks;
            let uploadedChunks = 0;

            // Step 2: Upload chunks sequentially
            const uploadChunks = (chunkIndex: number) => {
              if (isCancelled) {
                observer.complete();
                return;
              }

              if (chunkIndex >= totalChunks) {
                // Step 3: All chunks uploaded, finalize
                console.log('All chunks uploaded, finalizing...');
                this.finalizeResumableUpload(uploadId).subscribe({
                  next: (finalizeResponse) => {
                    if (isCancelled) {
                      observer.complete();
                      return;
                    }

                    console.log('Finalize upload response:', finalizeResponse);

                    // Check if response has success field (new format) or required fields (direct format)
                    const hasSuccessField = finalizeResponse && 'success' in finalizeResponse;
                    const hasFileField = finalizeResponse && finalizeResponse.file;

                    // If response has success field, check it
                    if (hasSuccessField && !finalizeResponse.success) {
                      const errorMessage = finalizeResponse?.message || 'Failed to finalize upload';
                      console.error('Failed to finalize upload:', errorMessage);
                      observer.error(new Error(errorMessage));
                      return;
                    }

                    // If we have file field or success is true, consider it successful
                    if (hasFileField || (hasSuccessField && finalizeResponse.success)) {
                      const response: UploadFileResponse = {
                        success: true,
                        message: 'File uploaded successfully',
                        file: finalizeResponse.file
                      };
                      if (onProgress) onProgress(100);
                      console.log('Upload finalized successfully');
                      observer.next(response);
                      observer.complete();
                    } else {
                      console.error('Failed to finalize upload: Invalid response', finalizeResponse);
                      observer.error(new Error('Failed to finalize upload: Invalid response from server'));
                    }
                  },
                  error: (error) => {
                    console.error('Error finalizing upload:', error);
                    observer.error(error);
                  }
                });
                return;
              }

              // Calculate chunk boundaries
              const start = chunkIndex * initResponse.chunkSize;
              const end = Math.min(start + initResponse.chunkSize, file.size);
              const chunk = file.slice(start, end);
              const isLastChunk = chunkIndex === totalChunks - 1;

              // Upload this chunk
              console.log(`Uploading chunk ${chunkIndex + 1}/${totalChunks}`);
              this.uploadChunk(uploadId, chunkIndex, chunk, isLastChunk).subscribe({
                next: (chunkResponse) => {
                  if (isCancelled) {
                    observer.complete();
                    return;
                  }

                  console.log(`Chunk ${chunkIndex + 1} upload response:`, chunkResponse);

                  // Check if response has success field (new format) or required fields (direct format)
                  const hasSuccessField = chunkResponse && 'success' in chunkResponse;
                  const hasRequiredFields = chunkResponse && ('chunkIndex' in chunkResponse || 'uploadedBytes' in chunkResponse);

                  // If response has success field, check it
                  if (hasSuccessField && !chunkResponse.success) {
                    const errorMessage = chunkResponse?.message || `Failed to upload chunk ${chunkIndex}`;
                    console.error(`Failed to upload chunk ${chunkIndex}:`, errorMessage);
                    observer.error(new Error(errorMessage));
                    return;
                  }

                  // If we have required fields or success is true, consider it successful
                  if (hasRequiredFields || (hasSuccessField && chunkResponse.success) || !hasSuccessField) {
                    uploadedChunks++;
                    const progress = Math.round((uploadedChunks / totalChunks) * 100);
                    if (onProgress) onProgress(progress);
                    console.log(`Chunk ${chunkIndex + 1} uploaded. Progress: ${progress}%`);

                    // Upload next chunk
                    uploadChunks(chunkIndex + 1);
                  } else {
                    console.error(`Failed to upload chunk ${chunkIndex}: Invalid response`, chunkResponse);
                    observer.error(new Error(`Failed to upload chunk ${chunkIndex}: Invalid response from server`));
                  }
                },
                error: (error) => {
                  console.error(`Error uploading chunk ${chunkIndex}:`, error);
                  observer.error(error);
                }
              });
            };

            // Start uploading chunks
            uploadChunks(0);
          },
          error: (error) => {
            console.error('Error initiating upload:', error);
            observer.error(error);
          }
        });
    });
  }

  // User Information Methods
  getStorageQuota(): Observable<StorageQuotaResponse> {
    return this.http.get<StorageQuotaResponse>(`${this.baseUrl}/quota`)
      .pipe(catchError(this.handleError));
  }

  // Note: Permissions are now determined by AppUserType, not a separate API

  // Utility Methods
  downloadFileDirectly(filePath: string, fromAdmin = false): void {
    let params = new HttpParams().set('filePath', filePath);
    if (fromAdmin) {
      params = params.set('fromAdmin', 'true');
    }

    const url = `${this.baseUrl}/download?${params.toString()}`;

    // Fetch as blob to ensure proper download
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        // Create object URL from the blob
        const objectUrl = window.URL.createObjectURL(blob);

        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = this.getFileNameFromPath(filePath);
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      },
      error: (error) => {
        console.error('Download failed:', error);
      }
    });
  }

  downloadFileWithSignedUrl(filePath: string, fromAdmin = false, fromOrganization = false): Observable<void> {
    return this.getSignedUrl(filePath, fromAdmin, fromOrganization).pipe(
      switchMap((response: SignedUrlResponse) => {
        if (!response.url) {
          throw new Error('No download URL received from server');
        }

        // Check if it's a local file URL (development environment)
        if (response.url.startsWith('file://')) {
          //console.log('Local file URL detected, falling back to direct download method');
          // For local development, fall back to the original download method
          // Note: downloadFileDirectly doesn't support fromOrganization, but for local dev it should work
          this.downloadFileDirectly(filePath, fromAdmin);
          return new Observable<void>(observer => {
            observer.next(undefined);
            observer.complete();
          });
        }

        // For production/cloud URLs, fetch the file content using the signed URL
        return this.http.get(response.url, { responseType: 'blob' }).pipe(
          map((blob: Blob) => {
            // Create object URL from the blob
            const objectUrl = window.URL.createObjectURL(blob);

            // Create a temporary link to trigger the download
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = this.getFileNameFromPath(filePath);
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
          })
        );
      })
    );
  }

  /**
   * Builds the full storage path for a file inside the signed-in user's own
   * storage area - the "My Files" tab of the Files page. The API roots that area
   * at users/<sanitized email>/, and endpoints that take a raw bucket path (such
   * as the dataset export) write wherever they are pointed, so callers have to
   * supply the prefix themselves for the file to show up in My Files.
   *
   * Keep sanitizeEmailForFolder in step with the API's SanitizeEmailForFolder.
   * @param userEmail The signed-in user's email (from selectCurrentUserEmail)
   * @param relativePath Optional path within the user's folder, e.g. "Exports/data.csv"
   */
  getUserStoragePath(userEmail: string, relativePath?: string): string {
    const userRoot = `users/${this.sanitizeEmailForFolder(userEmail)}`;
    const trimmed = relativePath?.replace(/^\/+|\/+$/g, '');

    return trimmed ? `${userRoot}/${trimmed}` : userRoot;
  }

  private sanitizeEmailForFolder(email: string): string {
    return email
      .replace(/@/g, '_at_')
      .replace(/\./g, '_')
      .replace(/\+/g, '_plus_')
      .toLowerCase();
  }

  getFileNameFromPath(filePath: string): string {
    return filePath.split('/').pop() || 'download';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(contentType: string): string {
    if (!contentType) return 'insert_drive_file';

    const type = contentType.toLowerCase();
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'videocam';
    if (type.startsWith('audio/')) return 'audiotrack';
    if (type.includes('pdf')) return 'picture_as_pdf';
    if (type.includes('word')) return 'description';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'table_chart';
    if (type.includes('powerpoint') || type.includes('presentation')) return 'slideshow';
    if (type.includes('zip') || type.includes('compressed')) return 'archive';
    return 'insert_drive_file';
  }

  isImageFile(contentType: string): boolean {
    return contentType && contentType.toLowerCase().startsWith('image/');
  }

  private handleError = (error: any): Observable<never> => {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = `Server error: ${error.status}`;
      }
    }

    console.error('FileManagementService error:', error);
    return throwError(() => ({ message: errorMessage, error: error.error }));
  };
}
